import { useId } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  FunctionIcon,
  SlidersHorizontalIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  workbookColumnSchema,
  type WorkbookColumnType,
  type WorkbookDiagnostic,
  type WorkbookColumnarSheet,
} from '@erp/shared-types';
import { isNameDiagnostic } from './WorksheetDiagnostics';
import { useColumnTypeOptions } from './worksheetTypeLabels';
import { sourceTypeUpdate } from './workbookCatalog';
import { WorksheetFormulaInput } from './WorksheetFormulaInput';
import { WorksheetColumnOrder } from './WorksheetColumnOrder';
import type { FormulaFields } from './worksheetFormulaFields';
import type { ChangeSheet } from './useWorkbookEditor';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

export function WorksheetColumnControls({
  sheet,
  formulaFields,
  readOnly,
  index,
  diagnostics,
  sourceTypes,
  change,
  move,
  rearrange,
  remove,
}: {
  sheet: WorkbookColumnarSheet;
  /** The Fields this column's Formula may reference. */
  formulaFields: FormulaFields;
  readOnly: boolean;
  index: number;
  /** This column's Diagnostics. */
  diagnostics: readonly WorkbookDiagnostic[];
  /** The types the Worksheet's input Source gives its columns, by name. */
  sourceTypes?: ReadonlyMap<string, WorkbookColumnType>;
  change: ChangeSheet;
  move: (delta: number) => void;
  /** Puts the columns in `order`, each entry a column's current index. */
  rearrange: (order: number[]) => void;
  remove: () => void;
}) {
  const gt = useGT();
  const column = sheet.columns[index];
  if (!column) return null;
  const invalidName = diagnostics.some(isNameDiagnostic);
  const invalidFormula = diagnostics.some(
    (diagnostic) => !isNameDiagnostic(diagnostic),
  );
  function updateColumn(patch: Partial<WorkbookColumn>) {
    change((latest) => ({
      ...latest,
      columns: latest.columns.map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    }));
  }
  return (
    <div className="flex items-center gap-2 border border-b-0 bg-muted/20 p-2">
      <label className="shrink-0">
        <span className="sr-only">
          <T context="worksheet selected column label">Column</T>
        </span>
        <Input
          className="w-32"
          aria-label={gt('Column name', {
            context: 'worksheet column name input',
          })}
          aria-invalid={invalidName || undefined}
          value={column.name}
          onChange={(event) => {
            const name = event.target.value;
            change((latest) => {
              const previous = latest.columns[index]?.name;
              return {
                ...latest,
                columns: latest.columns.map((item, i) =>
                  i === index ? { ...item, name } : item,
                ),
                filters: latest.filters.map((filter) =>
                  filter.column === previous
                    ? { ...filter, column: name }
                    : filter,
                ),
                sort: latest.sort.map((item) =>
                  item.column === previous ? { ...item, column: name } : item,
                ),
              };
            });
          }}
        />
      </label>

      <FunctionIcon size={18} className="shrink-0 text-muted-foreground" />
      <WorksheetFormulaInput
        value={column.expression}
        fields={formulaFields}
        readOnly={readOnly}
        label={gt('Formula', { context: 'worksheet formula input' })}
        invalid={invalidFormula}
        onChange={(expression) => updateColumn({ expression })}
      />
      <ColumnSettings
        sheet={sheet}
        index={index}
        readOnly={readOnly}
        sourceTypes={sourceTypes}
        updateColumn={updateColumn}
        move={move}
        remove={remove}
      />
      <WorksheetColumnOrder sheet={sheet} rearrange={rearrange} />
    </div>
  );
}

type WorkbookColumn = WorkbookColumnarSheet['columns'][number];

/** The selected column's settings: its type, number format, place and removal. */
function ColumnSettings({
  sheet,
  index,
  readOnly,
  sourceTypes,
  updateColumn,
  move,
  remove,
}: {
  sheet: WorkbookColumnarSheet;
  index: number;
  readOnly: boolean;
  sourceTypes?: ReadonlyMap<string, WorkbookColumnType>;
  updateColumn: (patch: Partial<WorkbookColumn>) => void;
  move: (delta: number) => void;
  remove: () => void;
}) {
  const gt = useGT();
  const typeLabelId = useId();
  const types = useColumnTypeOptions();
  const column = sheet.columns[index];
  if (!column) return null;
  const sourceType = sourceTypeUpdate(column, sourceTypes);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={gt('Column settings', {
            context: 'worksheet column settings',
          })}
        >
          <SlidersHorizontalIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label={gt('Column settings', {
          context: 'worksheet column settings',
        })}
      >
        <fieldset disabled={readOnly} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span id={typeLabelId} className="text-xs font-medium">
              <T context="worksheet column type label">Type</T>
            </span>
            <Select
              disabled={readOnly}
              value={column.type}
              onValueChange={(value) =>
                updateColumn({
                  type: workbookColumnSchema.shape.type.parse(value),
                })
              }
            >
              <SelectTrigger aria-labelledby={typeLabelId} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {types.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {sourceType && (
              <Button
                size="sm"
                variant="outline"
                className="self-start"
                onClick={() => updateColumn({ type: sourceType })}
              >
                {gt('Use {type} from the source', {
                  context: 'worksheet update column type from its source',
                  type:
                    types.find((option) => option.value === sourceType)
                      ?.label ?? sourceType,
                })}
              </Button>
            )}
          </div>
          {/* A format already set on another type stays visible, so it can be cleared. */}
          {(column.type === 'decimal' || column.numberFormat) && (
            <label className="flex flex-col gap-1.5 text-xs font-medium">
              <T context="worksheet number format label">Number format</T>
              <Input
                className="w-full font-normal"
                placeholder={gt('e.g. #,##0.00', {
                  context: 'worksheet number format placeholder',
                })}
                value={column.numberFormat ?? ''}
                onChange={(event) =>
                  updateColumn({ numberFormat: event.target.value })
                }
              />
            </label>
          )}
          <div className="flex flex-wrap items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={index === 0}
              onClick={() => move(-1)}
            >
              <ArrowLeftIcon />
              <T context="worksheet reorder column">Move left</T>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={index === sheet.columns.length - 1}
              onClick={() => move(1)}
            >
              <ArrowRightIcon />
              <T context="worksheet reorder column">Move right</T>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-destructive"
              disabled={sheet.columns.length === 1}
              onClick={remove}
            >
              <TrashIcon />
              <T context="worksheet remove column">Remove column</T>
            </Button>
          </div>
        </fieldset>
      </PopoverContent>
    </Popover>
  );
}
